import { Injectable, signal } from '@angular/core';

export interface AuthConfig {
  googleClientId?: string;
  facebookAppId?: string;
  appleClientId?: string;
  amazonClientId?: string;
}

export interface SocialAuthData {
  provider: 'GOOGLE' | 'FACEBOOK' | 'APPLE' | 'AMAZON' | '';
  accessToken: string;
}

@Injectable({ providedIn: 'root' })
export class SocialAuthService {
  private readonly _authConfig = signal<AuthConfig>({
    googleClientId: '',
    facebookAppId: '',
    appleClientId: '',
    amazonClientId: '',
  });
  private readonly _socialAuthData = signal<SocialAuthData | null>(null);

  // SDK loaded state signals
  private readonly _isGoogleSDKLoaded = signal<boolean>(false);
  private readonly _isGoogleAuthInprogress = signal<boolean>(false);
  private readonly _isFacebookSDKLoaded = signal<boolean>(false);
  private readonly _isFacebookAuthInprogress = signal<boolean>(false);
  private readonly _isAppleSDKLoaded = signal<boolean>(false);
  private readonly _isAppleAuthInprogress = signal<boolean>(false);
  private readonly _isAmazonSDKLoaded = signal<boolean>(false);
  private readonly _isAmazonAuthInprogress = signal<boolean>(false);

  // Expose the signals as readonly properties
  public readonly isGoogleSDKLoaded = this._isGoogleSDKLoaded.asReadonly();
  public readonly isGoogleAuthInprogress = this._isGoogleAuthInprogress.asReadonly();
  public readonly isFacebookSDKLoaded = this._isFacebookSDKLoaded.asReadonly();
  public readonly isFacebookAuthInprogress = this._isFacebookAuthInprogress.asReadonly();
  public readonly isAppleSDKLoaded = this._isAppleSDKLoaded.asReadonly();
  public readonly isAppleAuthInprogress = this._isAppleAuthInprogress.asReadonly();
  public readonly isAmazonSDKLoaded = this._isAmazonSDKLoaded.asReadonly();
  public readonly isAmazonAuthInprogress = this._isAmazonAuthInprogress.asReadonly();

  public readonly authConfig = this._authConfig.asReadonly();
  public readonly userProfile = this._socialAuthData.asReadonly();

  /**
   * Sets the auth config for social login and loads the respective SDKs
   */
  public setAuthConfig(config: any) {
    if (!config) {
      return;
    }

    this._authConfig.set({
      googleClientId: config?.GOOGLE ?? '',
      facebookAppId: config?.FACEBOOK ?? '',
      appleClientId: config?.APPLE ?? '',
      amazonClientId: config?.AMAZON ?? '',
    });

    // Load the SDKs based on the configuration
    if (this._authConfig()?.googleClientId) {
      this.loadGoogleSDK();
    }

    if (this._authConfig()?.facebookAppId) {
      this.loadFacebookSDK();
    }

    if (this._authConfig()?.appleClientId) {
      this.loadAppleScript();
    }

    if (this._authConfig()?.amazonClientId) {
      this.loadAmazonSDK();
    }
  }

  /* ------------------------- GOOGLE SIGN IN - START ------------------------- */
  /**
   * Loads the Google SDK script asynchronously if it is not already loaded
   */
  public loadGoogleSDK() {
    if (document.querySelector('script#google-sdk-js')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-sdk-js';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this._isGoogleSDKLoaded.set(true);
      console.log('Google SDK loaded');
    };
    script.onerror = (error) => {
      this._isGoogleSDKLoaded.set(false);
      console.error('Error loading Google SDK:', error);
    };
    document.body.appendChild(script);
  }

  /**
   * Initiates the Google sign-in process
   */
  public signInWithGoogle() {
    if (!this.isGoogleSDKLoaded() || !(window as any).google?.accounts?.oauth2 || !this._authConfig()) {
      console.error('Google OAuth client not loaded yet');
      return;
    }

    this._isGoogleAuthInprogress.set(true);
    const tokenClient = (window as any).google?.accounts?.oauth2.initTokenClient({
      client_id: this._authConfig()?.googleClientId,
      scope: 'openid email profile',
      callback: (response: any) => this.onGoogleSignInSuccess(response),
      error_callback: (error: any) => this.onGoogleSignInError(error),
    });
    tokenClient.requestAccessToken({ prompt: 'consent' });
  }

  /**
   * Handles the success response from Google sign-in
   */
  private onGoogleSignInSuccess(response: any) {
    if (response?.access_token) {
      this._isGoogleAuthInprogress.set(false);

      this._socialAuthData.set({
        provider: 'GOOGLE',
        accessToken: response.access_token,
      });
    } else {
      this._isGoogleAuthInprogress.set(false);
      console.error('Error during Google sign-in:', response);
    }
  }

  /**
   * Handles the error response from Google sign-in
   */
  private onGoogleSignInError(error: any) {
    this._isGoogleAuthInprogress.set(false);
    this._socialAuthData.set(null);

    if (error.type === 'popup_failed_to_open') {
      console.log('Popup failed to open', error);
    } else if (error.type === 'popup_closed') {
      console.log('Popup closed', error);
    } else {
      console.error('Error during Google sign-in:', error);
    }
  }

  /* -------------------------- GOOGLE SIGN IN - END -------------------------- */

  /* ------------------------ FACEBOOK SIGN IN - START ------------------------ */
  /**
   * Loads the Facebook SDK script if it is not already loaded
   */
  private loadFacebookSDK() {
    if (document.querySelector('script#facebook-sdk-js')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-sdk-js';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      this._isFacebookSDKLoaded.set(true);
      console.log('Facebook SDK loaded');

      // Initialize the Facebook SDK
      (window as any).fbAsyncInit = this.initFacebookSDK.bind(this);
    };
    script.onerror = (error) => {
      this._isFacebookSDKLoaded.set(false);
      console.error('Error loading Facebook SDK:', error);
    };

    document.body.appendChild(script);
  }

  /**
   * Initializes the Facebook SDK with the provided configuration
   */
  private initFacebookSDK() {
    (window as any).FB.init({
      appId: this._authConfig()?.facebookAppId,
      cookie: false,
      xfbml: false,
      version: 'v22.0',
    });
  }

  /**
   * Initiates the sign-in process with Facebook and handles the response
   */
  public signInWithFacebook(): void {
    if (!this.isFacebookSDKLoaded()) {
      console.error('Facebook SDK not loaded yet.');
      return;
    }

    this._isFacebookAuthInprogress.set(true);
    (window as any).FB.login(
      (response: any) => {
        this._isFacebookAuthInprogress.set(false);
        if (response.status === 'connected') {
          this._isFacebookAuthInprogress.set(false);
          this._socialAuthData.set({
            provider: 'FACEBOOK',
            accessToken: response.authResponse.accessToken,
          });
        } else {
          this._isFacebookAuthInprogress.set(false);
          this._socialAuthData.set(null);
          console.error('Facebook login failed:', response);
        }
      },
      { scope: 'public_profile,email' },
    );
  }
  /* ------------------------- FACEBOOK SIGN IN - END ------------------------- */

  /* -------------------------- APPLE SIGN IN - START ------------------------- */
  /**
   * Loads the Apple SDK script if it is not already loaded
   */
  private loadAppleScript() {
    if (document.querySelector('script#apple-sdk-js')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'apple-sdk-js';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this._isAppleSDKLoaded.set(true);
      console.log('Apple SDK loaded');
    };
    script.onerror = (error) => {
      this._isAppleSDKLoaded.set(false);
      console.error('Error loading Apple SDK:', error);
    };
    document.body.appendChild(script);
  }

  /**
   * Initiates the Apple sign-in process
   */
  public async signInWithApple() {
    if (!this.isAppleSDKLoaded()) {
      console.error('Apple SDK not loaded yet.');
      return;
    }

    this._isAppleAuthInprogress.set(true);
    (window as any)?.AppleID?.auth?.init({
      clientId: this._authConfig()?.appleClientId,
      scope: 'name email',
      redirectURI: window.location.origin + '/apple-callback',
      state: 'init',
      nonce: 'test',
      usePopup: true,
    });

    try {
      const data = await (window as any)?.AppleID?.auth?.signIn();
      console.log(this.parseJwt(data.authorization.id_token));
      this._isAppleAuthInprogress.set(false);

      this._socialAuthData.set({
        provider: 'APPLE',
        accessToken: data.authorization.id_token,
      });
    } catch (error) {
      console.error('Error during Apple sign-in:', error);
      this._isAppleAuthInprogress.set(false);
      this._socialAuthData.set(null);
    }
  }

  /**
   * Parses the JWT token to extract the user information
   */
  private parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  }
  /* --------------------------- APPLE SIGN IN - END -------------------------- */

  /* ------------------------- AMAZON SIGN IN - START ------------------------- */
  /**
   * Loads the Amazon SDK script if it is not already loaded
   */
  private loadAmazonSDK() {
    if (document.querySelector('script#amazon-sdk-js')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'amazon-sdk-js';
    script.src = 'https://assets.loginwithamazon.com/sdk/na/login1.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this._isAmazonSDKLoaded.set(true);
      console.log('Amazon SDK loaded');
    };
    script.onerror = (error) => {
      this._isAmazonSDKLoaded.set(false);
      console.error('Error loading Amazon SDK:', error);
    };
    document.body.appendChild(script);
  }

  /**
   * Initializes the Amazon SDK with the provided configuration
   */
  private initAmazonSDK() {
    (window as any).amazon.Login.setClientId(this._authConfig()?.amazonClientId);
  }

  /**
   * Initiates the Amazon sign-in process and handles the response
   */
  public signInWithAmazon() {
    if (!this.isAmazonSDKLoaded()) {
      console.error('Amazon SDK not loaded yet.');
      return;
    }

    this._isAmazonAuthInprogress.set(true);
    this.initAmazonSDK();

    (window as any).amazon.Login.authorize(
      { scope: 'profile', popup: true, interactive: 'always' },
      (response: any) => {
        if (response.error) {
          console.error('Error during Amazon sign-in:', response.error);
          this._isAmazonAuthInprogress.set(false);
          this._socialAuthData.set(null);
        } else {
          this._isAmazonAuthInprogress.set(false);
          this._socialAuthData.set({
            provider: 'AMAZON',
            accessToken: response?.access_token,
          });
        }
      },
    );
  }
  /* -------------------------- AMAZON SIGN IN - END -------------------------- */

  /**
   * Resets the social auth data
   */
  public resetAuthData() {
    this._socialAuthData.set(null);
  }

  /**
   * Fetches user profile from Google using the access token
   */
  public async fetchGoogleProfile(accessToken: string): Promise<any> {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v1/userinfo?alt=json', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Google profile');
      }

      const profile = await response.json();
      return {
        email: profile.email,
        firstName: profile.given_name || '',
        lastName: profile.family_name || '',
        fullName: profile.name || '',
        picture: profile.picture || '',
      };
    } catch (error) {
      console.error('Error fetching Google profile:', error);
      return null;
    }
  }

  /**
   * Fetches user profile from Facebook using the access token
   */
  public async fetchFacebookProfile(accessToken: string): Promise<any> {
    try {
      const response = await fetch(
        `https://graph.facebook.com/me?fields=id,name,email,first_name,last_name,picture&access_token=${accessToken}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch Facebook profile');
      }

      const profile = await response.json();
      return {
        email: profile.email,
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        fullName: profile.name || '',
        picture: profile.picture?.data?.url || '',
      };
    } catch (error) {
      console.error('Error fetching Facebook profile:', error);
      return null;
    }
  }

  /**
   * Fetches user profile from Amazon using the access token
   */
  public async fetchAmazonProfile(accessToken: string): Promise<any> {
    try {
      const response = await fetch('https://api.amazon.com/user/profile', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Amazon profile');
      }

      const profile = await response.json();
      return {
        email: profile.email,
        firstName: profile.name?.split(' ')[0] || '',
        lastName: profile.name?.split(' ').slice(1).join(' ') || '',
        fullName: profile.name || '',
        picture: '',
      };
    } catch (error) {
      console.error('Error fetching Amazon profile:', error);
      return null;
    }
  }
}

